import React, { useState } from 'react';
import { Sparkles, FileText, CheckCircle, MessageSquare, X } from 'lucide-react';
import { Button } from '../ui/button';

const AgentOnboardingModal = ({ onClose }) => {
  const [step, setStep] = useState(1);

  const handleFinish = () => {
    localStorage.setItem('lexy_agent_onboarding_seen', 'true');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full shadow-2xl">
        {/* Header */}
        <div className="relative p-6 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-purple-50">
          <button
            onClick={handleFinish}
            className="absolute top-4 right-4 p-1 hover:bg-white/50 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-indigo-600 rounded-lg">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Velkommen til AI Agenter!</h2>
              <p className="text-sm text-gray-600 mt-1">La oss vise deg hvordan dette fungerer</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-8">
          {step === 1 && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="inline-flex p-4 bg-gray-100 rounded-full mb-4">
                  <Sparkles className="w-12 h-12 text-indigo-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Hva er AI Agenter?</h3>
                <p className="text-gray-600 max-w-md mx-auto">
                  AI Agenter er proaktive assistenter som analyserer sakene dine automatisk og gir deg smarte forslag.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <FileText className="w-8 h-8 text-blue-600 mb-2" />
                  <h4 className="font-semibold text-gray-900 text-sm mb-1">DokumentAgent</h4>
                  <p className="text-xs text-gray-600">Analyserer dokumenter og identifiserer mangler</p>
                </div>

                <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <CheckCircle className="w-8 h-8 text-orange-600 mb-2" />
                  <h4 className="font-semibold text-gray-900 text-sm mb-1">RisikoAgent</h4>
                  <p className="text-xs text-gray-600">Identifiserer juridiske risikoer i saken</p>
                </div>

                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <MessageSquare className="w-8 h-8 text-green-600 mb-2" />
                  <h4 className="font-semibold text-gray-900 text-sm mb-1">OppgaveAgent</h4>
                  <p className="text-xs text-gray-600">Foreslår neste oppgaver basert på saksstatus</p>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="inline-flex p-4 bg-green-100 rounded-full mb-4">
                  <CheckCircle className="w-12 h-12 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Hvordan bruker du det?</h3>
                <p className="text-gray-600 max-w-md mx-auto">
                  Det er enkelt! Følg disse trinnene:
                </p>
              </div>

              <div className="space-y-4 max-w-lg mx-auto">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold">
                    1
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Kjør en agent</h4>
                    <p className="text-sm text-gray-600">
                      Klikk \"Kjør agent\" på et av agent-kortene nedenfor
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold">
                    2
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Se forslagene</h4>
                    <p className="text-sm text-gray-600">
                      Agenten analyserer saken og viser forslag du kan godkjenne eller avvise
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold">
                    3
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Godkjenn eller avvis</h4>
                    <p className="text-sm text-gray-600">
                      Du har full kontroll. Ingen AI-handling skjer uten din godkjenning.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold">
                    4
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Gi feedback</h4>
                    <p className="text-sm text-gray-600">
                      Hjelp oss forbedre agentene ved å gi tilbakemelding på forslagene
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="inline-flex p-4 bg-purple-100 rounded-full mb-4">
                  <Sparkles className="w-12 h-12 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Pro tips!</h3>
              </div>

              <div className="space-y-3 max-w-lg mx-auto">
                <div className="p-4 bg-gray-50 border border-indigo-200 rounded-lg">
                  <h4 className="font-semibold text-indigo-900 text-sm mb-1">💡 Auto-trigger</h4>
                  <p className="text-xs text-indigo-700">
                    Aktiver auto-trigger i innstillinger, så kjører DokumentAgent automatisk når du laster opp dokumenter.
                  </p>
                </div>

                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <h4 className="font-semibold text-green-900 text-sm mb-1">✅ Kvalitet</h4>
                  <p className="text-xs text-green-700">
                    Jo mer feedback du gir, desto bedre blir agentene. Din tilbakemelding hjelper oss forbedre systemet.
                  </p>
                </div>

                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <h4 className="font-semibold text-amber-900 text-sm mb-1">⚡ Effektivitet</h4>
                  <p className="text-xs text-amber-700">
                    Agentene jobber i bakgrunnen og tar ca 10-15 sekunder. Du kan jobbe med noe annet mens de kjører.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex gap-2">
            {[1, 2, 3].map(s => (
              <div
                key={s}
                className={`w-2 h-2 rounded-full ${
                  s === step ? 'bg-indigo-600' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>

          <div className="flex gap-2">
            {step > 1 && (
              <Button
                onClick={() => setStep(step - 1)}
                variant="outline"
              >
                Tilbake
              </Button>
            )}

            {step < 3 ? (
              <Button
                onClick={() => setStep(step + 1)}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                Neste
              </Button>
            ) : (
              <Button
                onClick={handleFinish}
                className="bg-green-600 hover:bg-green-700"
              >
                Kom i gang! 🚀
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentOnboardingModal;
